import { Box, Tooltip } from "@mui/material";
import { isSentinel } from "@platform/formatting-utils";
import DOMPurify from "dompurify";

import { buildQuotesPopupCustomTextCellKey } from "./cellEditorOverride";

import type { CellViewRendererOverride } from "@platform/btx-plan-designer";
import type { CoverageTypeEnum } from "@platform/types";
import type { QuotesPopupCustomTextByCellKey } from "./cellEditorOverride";

/**
 * Custom text saved through the rich-text popup editor is HTML; custom text
 * saved before that editor existed is a plain string. Detecting a tag is
 * enough to tell them apart, since a legacy value is never itself markup.
 */
const HTML_TAG_PATTERN = /<[a-z][\s\S]*>/i;

/**
 * Exactly the tags/marks `RichTextEditor`'s TipTap config can produce
 * (bold, italic, underline, bullet/ordered lists, line breaks) — anything
 * else is stripped rather than trusted, since the field-overrides endpoint
 * accepts this string from any caller, not just the editor.
 */
const sanitizeRichCustomText = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "strong", "em", "u", "ul", "ol", "li", "br"],
    ALLOWED_ATTR: [],
  });

/**
 * The Accident MP configuration setting that prints the Coma row as "Included"
 * / "Not Included" instead of the quoted amount. The grid keeps showing what's
 * stored, since another configuration may print it, so a cell the MP will
 * print differently says what the MP prints beneath it. The rule matches the
 * MP composer's: a dollar-amount Coma type prints "Included", and a Coma with
 * no type prints "Not Included".
 */
export const COMA_INCLUSION_ONLY_KEY = "coma_included_not_included";

type ComaMpLabel = "Included" | "Not Included";

// Undefined where the grid already shows what the MP prints: a sentinel type
// carries the same label in both, and custom text is what the MP prints.
const comaMpLabel = (
  benefitKey: string,
  value: unknown,
  customText: string | undefined,
  comaInclusionOnly: boolean,
): ComaMpLabel | undefined => {
  if (
    !comaInclusionOnly ||
    customText !== undefined ||
    benefitKey !== "comaType"
  ) {
    return undefined;
  }
  if (value === "upToDollarAmount") return "Included";
  if (isSentinel(value)) return undefined;
  return "Not Included";
};

// One sentence per line: the tooltip renders the newlines.
const COMA_MP_NOTES: Record<ComaMpLabel, string> = {
  Included: "Displays as “Included” in the MP",
  "Not Included":
    "No value in the proposal.\nDisplays as “Not Included” in the MP",
};

/**
 * A Coma cell the MP prints differently from the grid: what's stored (a quoted
 * amount in parentheses, or the grid's empty-cell marker), above the label the
 * presentation prints in its place, marked with a red asterisk. It runs to two
 * lines, so it takes the block layout the multi-line cells use and lets the
 * row auto-grow.
 */
const ComaMpLabelCellView = ({
  text,
  mpLabel,
  isEditable,
}: {
  text: string;
  mpLabel: ComaMpLabel;
  isEditable: boolean;
}): React.JSX.Element => (
  <Box
    component="span"
    className="quotes-cell-view"
    sx={{
      display: "block",
      width: "100%",
      ...(isEditable && { cursor: "pointer" }),
    }}
  >
    <Box component="span" sx={{ display: "block" }}>
      {mpLabel === "Included" && text ? `(${text})` : text || "-"}
    </Box>
    <Box component="span" sx={{ display: "block" }}>
      {mpLabel}
      <Tooltip
        title={COMA_MP_NOTES[mpLabel]}
        slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      >
        <Box
          component="span"
          data-testid="coma-mp-label-note"
          sx={{ color: "error.main" }}
        >
          *
        </Box>
      </Tooltip>
    </Box>
  </Box>
);

/**
 * Builds a `CellViewRendererOverride` for the Quotes tab.
 *
 * Returns custom JSX for proposal cells — a single-line, ellipsis-clipped
 * text span, with a pointer-cursor edit affordance added for editable
 * cells. Saved custom text that's rich-text HTML instead renders as
 * sanitized markup in a block layout. The text content itself is already
 * resolved by the `CellFormatOverride` layer (cancer formatters, etc.) and
 * arrives here as `defaultText`. We just wrap it in richer UI.
 *
 * Cells that belong to the "current" (requested) column are read-only —
 * they get the same ellipsis clipping, just without the edit affordance.
 *
 * When the caller passes a `fallbackNode` (a benefit with its own dedicated
 * cell-renderer component, e.g. Service Waiting Periods' list) and this
 * cell has no saved custom text, we return `undefined` so the caller shows
 * that richer renderer instead of this plain text span.
 *
 * `comaInclusionOnly` is the RFP's MP configuration setting under
 * {@link COMA_INCLUSION_ONLY_KEY}.
 */
export const buildQuotesCellViewRenderer = (
  _selectedCoverageType: CoverageTypeEnum | undefined,
  customTextByCellKey?: QuotesPopupCustomTextByCellKey,
  comaInclusionOnly = false,
): CellViewRendererOverride | null => {
  // Return null if there's nothing to override (the SbsCellRenderer
  // will just render its default <Box component="span">{text}</Box>,
  // which has no overflow/ellipsis clipping). Otherwise every cell gets
  // ellipsis clipping (editable ones on top of that getting the
  // pointer-cursor affordance) — except a cell with a richer fallbackNode
  // and no custom text, which defers to that fallback instead.
  const renderer: CellViewRendererOverride = (
    params,
    defaultText,
    fallbackNode,
  ) => {
    // Skip non-leaf cells (group rows, benefit label column, etc.)
    const colField = params.colDef?.field;
    if (!colField) return undefined;

    // The "requested" (current) column is read-only — no edit affordance.
    const data = params.data as { benefitKey?: string } | undefined;
    if (!data?.benefitKey) return undefined;

    // Only proposal (non-pinned) leaf columns get the affordance.
    // The column field for plan data contains the plan identifier (e.g.
    // "plan123.benefits.diagnosisBenefit" or just "plan123"). If the
    // cell is not editable per AG Grid's column definition, skip it.
    const isEditable =
      typeof params.colDef?.editable === "function"
        ? params.colDef.editable(params as never)
        : params.colDef?.editable;

    const cellKey = buildQuotesPopupCustomTextCellKey(params);
    const customText = cellKey ? customTextByCellKey?.get(cellKey) : undefined;

    // No saved override for this cell: when the caller has a richer renderer
    // of its own (e.g. Service Waiting Periods' list), defer to it instead of
    // replacing it with this plain ellipsis-clipped text span.
    if (customText === undefined && fallbackNode !== undefined) {
      return undefined;
    }

    const displayedText = customText ?? defaultText;

    // Custom text saved through the rich-text popup editor: render the
    // (sanitized) markup rather than the raw tags. Rich content — lists,
    // multiple paragraphs — doesn't fit the single-line ellipsis model, so
    // it always takes the block/auto-height layout, same as the legacy
    // multi-line case below.
    if (
      typeof displayedText === "string" &&
      HTML_TAG_PATTERN.test(displayedText)
    ) {
      return (
        <Box
          component="span"
          className="quotes-cell-view"
          sx={{
            display: "block",
            width: "100%",
            "& li p": { textAlign: "left" },
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichCustomText(displayedText),
          }}
        />
      );
    }

    // Multi-line values (e.g. Service Waiting Periods, formatted as one
    // entry per line) render with each entry on its own line and let the
    // row auto-grow. The grid's `autoHeight` measures the rendered height.
    const isMultiLine =
      typeof displayedText === "string" && displayedText.includes("\n");
    if (isMultiLine) {
      return (
        <Box
          component="span"
          className="quotes-cell-view"
          sx={{ display: "block", width: "100%", whiteSpace: "pre-line" }}
        >
          {displayedText}
        </Box>
      );
    }

    const mpLabel = comaMpLabel(
      data.benefitKey,
      params.value,
      customText,
      comaInclusionOnly,
    );
    if (mpLabel) {
      return (
        <ComaMpLabelCellView
          text={displayedText}
          mpLabel={mpLabel}
          isEditable={Boolean(isEditable)}
        />
      );
    }

    // Read-only (requested) column cells still need the same single-line
    // ellipsis clipping as editable cells — they just skip the pointer
    // cursor affordance. Empty editable cells still get the affordance
    // (shows pencil on hover so the user knows they can click to enter
    // a value).
    return (
      <Box
        component="span"
        className="quotes-cell-view"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          width: "100%",
          ...(isEditable && { cursor: "pointer" }),
        }}
      >
        <Box
          component="span"
          sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {displayedText || "-"}
        </Box>
      </Box>
    );
  };

  return renderer;
};
